import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import Select from "react-select";
import cogoToast from "cogo-toast";
import { SUCCESS_RESULT } from "../../Constants";
import { genericGet, genericPost } from "../../services/Api";

const PROJECT_VALUES_ENDPOINT = "unit4/project-mappings/get-unique-field-value?feild_name=project";
const ACTIVITY_VALUES_ENDPOINT = "unit4/project-mappings/get-unique-field-value?feild_name=activity";
const LIST_MAPPINGS_ENDPOINT = "unit4/project-mappings/list";
const toNumber = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const fmt = (value) =>
  toNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const truncateText = (text, max = 50) => {
  const value = String(text || "");
  if (value.length <= max) return value;
  return `${value.slice(0, max)}...`;
};

const ErpProjectMapping = () => {
  const [step, setStep] = useState(1);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedActivity, setSelectedActivity] = useState("");
  const [totalPrAmount, setTotalPrAmount] = useState("");
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);
  const [mappingRows, setMappingRows] = useState([]);

  const copyToClipboard = async (label, value) => {
    const text = String(value ?? "");
    if (!text || text === "-") return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      cogoToast.success(`${label} copied`, { hideAfter: 2 });
    } catch (e) {
      cogoToast.error("Failed to copy", { hideAfter: 3 });
    }
  };

  const calculatedRows = useMemo(() => {
    const totalBudget = mappingRows.reduce((sum, row) => sum + toNumber(row?.budget), 0);
    const prAmount = toNumber(totalPrAmount);
    return mappingRows.map((row) => {
      const budget = toNumber(row?.budget);
      const ratio = totalBudget > 0 ? budget / totalBudget : 0;
      return {
        ...row,
        _budgetNumber: budget,
        _percentageRatio: ratio,
        _percentageValue: ratio * 100,
        _lineAmount: prAmount * ratio,
      };
    });
  }, [mappingRows, totalPrAmount]);

  const totals = useMemo(() => {
    return calculatedRows.reduce(
      (acc, row) => {
        acc.budget += toNumber(row?._budgetNumber);
        acc.percentage += toNumber(row?._percentageValue);
        acc.lineAmount += toNumber(row?._lineAmount);
        return acc;
      },
      { budget: 0, percentage: 0, lineAmount: 0 }
    );
  }, [calculatedRows]);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 46,
      fontSize: "1.02rem",
      borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.15rem rgba(13,110,253,.2)" : "none",
      "&:hover": { borderColor: "#86b7fe" },
    }),
    menu: (base) => ({ ...base, zIndex: 9999, fontSize: "1.02rem" }),
    singleValue: (base) => ({ ...base, fontSize: "1.02rem" }),
    option: (base) => ({ ...base, fontSize: "1.02rem" }),
    placeholder: (base) => ({ ...base, fontSize: "1.02rem" }),
  };

  const projectSelectOptions = projectOptions.map((value) => ({ value, label: value }));
  const activitySelectOptions = activityOptions.map((value) => ({ value, label: value }));

  const tableHeaderStyle = {
    backgroundColor: "#eef4ff",
    color: "#1e3a5f",
    fontWeight: 700,
    fontSize: "0.76rem",
    whiteSpace: "nowrap",
  };

  const tableCellStyle = {
    fontSize: "0.96rem",
    verticalAlign: "middle",
    paddingTop: "0.45rem",
    paddingBottom: "0.45rem",
  };

  const copyButtonStyle = {
    border: "1px solid #cfe0ff",
    background: "#e9f2ff",
    color: "#0d6efd",
    borderRadius: 6,
    width: 24,
    height: 24,
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  };

  const loadUniqueProjects = () => {
    setIsLoadingProjects(true);
    genericGet(PROJECT_VALUES_ENDPOINT)
      .then((res) => {
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          const values = Array.isArray(res?.data?.data?.values) ? res.data.data.values : [];
          setProjectOptions(values);
          return;
        }
        setProjectOptions([]);
        cogoToast.error(res?.data?.message || "Failed to load project values", { hideAfter: 4 });
      })
      .catch(() => {
        setProjectOptions([]);
        cogoToast.error("Failed to load project values", { hideAfter: 4 });
      })
      .finally(() => setIsLoadingProjects(false));
  };

  const loadUniqueActivities = () => {
    setIsLoadingActivities(true);
    genericGet(ACTIVITY_VALUES_ENDPOINT)
      .then((res) => {
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          const values = Array.isArray(res?.data?.data?.values) ? res.data.data.values : [];
          setActivityOptions(values);
          return;
        }
        setActivityOptions([]);
        cogoToast.error(res?.data?.message || "Failed to load activity values", { hideAfter: 4 });
      })
      .catch(() => {
        setActivityOptions([]);
        cogoToast.error("Failed to load activity values", { hideAfter: 4 });
      })
      .finally(() => setIsLoadingActivities(false));
  };

  useEffect(() => {
    loadUniqueProjects();
    loadUniqueActivities();
  }, []);

  const handleNext = () => {
    if (!selectedProject || !selectedActivity) {
      cogoToast.error("Select both project and activity", { hideAfter: 3 });
      return;
    }
    const parsedPr = toNumber(totalPrAmount);
    if (!totalPrAmount.trim() || parsedPr <= 0) {
      cogoToast.error("Enter valid Total PR Amount", { hideAfter: 3 });
      return;
    }
    setIsLoadingMappings(true);
    genericPost(LIST_MAPPINGS_ENDPOINT, {
      project: selectedProject,
      acvtivity: selectedActivity,
    })
      .then((res) => {
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          setMappingRows(Array.isArray(res?.data?.data) ? res.data.data : []);
          setStep(2);
          return;
        }
        setMappingRows([]);
        cogoToast.error(res?.data?.message || "Failed to load mappings", { hideAfter: 4 });
      })
      .catch(() => {
        setMappingRows([]);
        cogoToast.error("Failed to load mappings", { hideAfter: 4 });
      })
      .finally(() => setIsLoadingMappings(false));
  };

  return (
    <div style={{ padding: "20px", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <h2 style={{ marginBottom: 10 }}>ERP Project Mapping</h2>
      <Breadcrumb className="ml-1 mb-3">
        <BreadcrumbItem>
          <Link to="/dashboard">Dashboard</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Link to="/projects/list">Projects</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>ERP Project Mapping</BreadcrumbItem>
      </Breadcrumb>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="mb-3">
            <span className={`badge ${step === 1 ? "badge-primary" : "badge-secondary"} mr-2`}>Step 1</span>
            <span className={`badge ${step === 2 ? "badge-primary" : "badge-secondary"}`}>Step 2</span>
          </div>

          {step === 1 && (
            <>
              <h5 className="mb-3">Select Project and Activity</h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="font-weight-bold mb-1" style={{ fontSize: "1.02rem" }}>Project</label>
                  <Select
                    styles={selectStyles}
                    isSearchable
                    isClearable
                    isDisabled={isLoadingProjects}
                    options={projectSelectOptions}
                    value={projectSelectOptions.find((o) => o.value === selectedProject) || null}
                    onChange={(option) => setSelectedProject(option?.value || "")}
                    placeholder={isLoadingProjects ? "Loading..." : "Select project"}
                    noOptionsMessage={() => "No projects found"}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="font-weight-bold mb-1" style={{ fontSize: "1.02rem" }}>Activity</label>
                  <Select
                    styles={selectStyles}
                    isSearchable
                    isClearable
                    isDisabled={isLoadingActivities}
                    options={activitySelectOptions}
                    value={activitySelectOptions.find((o) => o.value === selectedActivity) || null}
                    onChange={(option) => setSelectedActivity(option?.value || "")}
                    placeholder={isLoadingActivities ? "Loading..." : "Select activity"}
                    noOptionsMessage={() => "No activities found"}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="font-weight-bold mb-1" style={{ fontSize: "1.02rem" }}>Total PR Amount</label>
                  <input
                    className="form-control"
                    style={{ fontSize: "1.02rem", minHeight: 46 }}
                    value={totalPrAmount}
                    onChange={(e) => setTotalPrAmount(e.target.value)}
                    placeholder="Enter total PR amount"
                  />
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleNext} disabled={isLoadingMappings}>
                {isLoadingMappings ? "Loading..." : "Next"}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h5 className="mb-3">Selected Values</h5>
              <div className="alert alert-light border">
                <div className="mb-2">
                  <span className="font-weight-bold">Project:</span> {selectedProject || "-"}
                </div>
                <div>
                  <span className="font-weight-bold">Activity:</span> {selectedActivity || "-"}
                </div>
                <div>
                  <span className="font-weight-bold">Total PR Amount:</span> <span className="font-weight-bold">{fmt(totalPrAmount)}</span>
                </div>
                <div className="mt-2" title={mappingRows[0]?.activity_desc || ""}>
                  <span className="font-weight-bold">Activity Desc:</span> {mappingRows[0]?.activity_desc || "-"}
                </div>
              </div>
              <div className="table-responsive mb-3">
                <table className="table table-sm table-bordered table-hover mb-0" style={{ borderColor: "#d9e6ff" }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Project</th>
                      <th style={tableHeaderStyle}>Workorder</th>
                      <th style={tableHeaderStyle}>Workorder Name</th>
                      <th style={tableHeaderStyle}>Activity</th>
                      <th style={tableHeaderStyle}>Budget</th>
                      <th style={tableHeaderStyle}>Percentage</th>
                      <th style={tableHeaderStyle}>Line Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculatedRows.length === 0 ? (
                      <tr>
                        <td className="text-center text-muted" colSpan={7} style={tableCellStyle}>
                          No records found
                        </td>
                      </tr>
                    ) : (
                      <>
                        {calculatedRows.map((row, index) => (
                          <tr key={row?.id || index}>
                            <td style={tableCellStyle}>
                              <div className="d-flex align-items-center" style={{ gap: 6 }}>
                                <span className="font-weight-bold">{row?.project || "-"}</span>
                                <button
                                  type="button"
                                  className="border-0"
                                  style={copyButtonStyle}
                                  title="Copy project"
                                  onClick={() => copyToClipboard("Project", row?.project)}
                                >
                                  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                    <path d="M10 1.5H3A1.5 1.5 0 0 0 1.5 3v9A1.5 1.5 0 0 0 3 13.5h7A1.5 1.5 0 0 0 11.5 12V3A1.5 1.5 0 0 0 10 1.5Zm.5 10.5a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v9Z" />
                                    <path d="M13 4.5a.5.5 0 0 1 .5.5v8A2.5 2.5 0 0 1 11 15.5H5a.5.5 0 0 1 0-1h6A1.5 1.5 0 0 0 12.5 13V5a.5.5 0 0 1 .5-.5Z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td style={tableCellStyle}>
                              <div className="d-flex align-items-center" style={{ gap: 6 }}>
                                <span className="font-weight-bold">{row?.workorder || "-"}</span>
                                <button
                                  type="button"
                                  className="border-0"
                                  style={copyButtonStyle}
                                  title="Copy workorder"
                                  onClick={() => copyToClipboard("Workorder", row?.workorder)}
                                >
                                  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                    <path d="M10 1.5H3A1.5 1.5 0 0 0 1.5 3v9A1.5 1.5 0 0 0 3 13.5h7A1.5 1.5 0 0 0 11.5 12V3A1.5 1.5 0 0 0 10 1.5Zm.5 10.5a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v9Z" />
                                    <path d="M13 4.5a.5.5 0 0 1 .5.5v8A2.5 2.5 0 0 1 11 15.5H5a.5.5 0 0 1 0-1h6A1.5 1.5 0 0 0 12.5 13V5a.5.5 0 0 1 .5-.5Z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td style={tableCellStyle}>{row?.workord_name || "-"}</td>
                            <td style={tableCellStyle}>
                              <div className="d-flex align-items-center" style={{ gap: 6 }}>
                                <span className="font-weight-bold">{row?.activity || "-"}</span>
                                <button
                                  type="button"
                                  className="border-0"
                                  style={copyButtonStyle}
                                  title="Copy activity"
                                  onClick={() => copyToClipboard("Activity", row?.activity)}
                                >
                                  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                    <path d="M10 1.5H3A1.5 1.5 0 0 0 1.5 3v9A1.5 1.5 0 0 0 3 13.5h7A1.5 1.5 0 0 0 11.5 12V3A1.5 1.5 0 0 0 10 1.5Zm.5 10.5a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v9Z" />
                                    <path d="M13 4.5a.5.5 0 0 1 .5.5v8A2.5 2.5 0 0 1 11 15.5H5a.5.5 0 0 1 0-1h6A1.5 1.5 0 0 0 12.5 13V5a.5.5 0 0 1 .5-.5Z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td style={tableCellStyle}>{fmt(row?._budgetNumber)}</td>
                            <td style={tableCellStyle}>{fmt(row?._percentageValue)}%</td>
                            <td style={tableCellStyle}>
                              <div className="d-flex align-items-center" style={{ gap: 6 }}>
                                <span className="font-weight-bold">{fmt(row?._lineAmount)}</span>
                                <button
                                  type="button"
                                  className="border-0"
                                  style={copyButtonStyle}
                                  title="Copy line amount"
                                  onClick={() => copyToClipboard("Line Amount", fmt(row?._lineAmount))}
                                >
                                  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                    <path d="M10 1.5H3A1.5 1.5 0 0 0 1.5 3v9A1.5 1.5 0 0 0 3 13.5h7A1.5 1.5 0 0 0 11.5 12V3A1.5 1.5 0 0 0 10 1.5Zm.5 10.5a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v9Z" />
                                    <path d="M13 4.5a.5.5 0 0 1 .5.5v8A2.5 2.5 0 0 1 11 15.5H5a.5.5 0 0 1 0-1h6A1.5 1.5 0 0 0 12.5 13V5a.5.5 0 0 1 .5-.5Z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        <tr className="font-weight-bold bg-light">
                          <td colSpan={4} className="text-right" style={tableCellStyle}>
                            Total
                          </td>
                          <td style={tableCellStyle}>{fmt(totals.budget)}</td>
                          <td style={tableCellStyle}>{fmt(totals.percentage)}%</td>
                          <td style={tableCellStyle}>{fmt(totals.lineAmount)}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              <button className="btn btn-outline-secondary mr-2" onClick={() => setStep(1)}>
                Back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErpProjectMapping;
